import { Button, buttonVariants } from '@/app/common/components/Button'
import FormErrorMessage from '@/app/common/components/FormErrorMessage'
import { Input } from '@/app/common/components/Input'
import FamilyWizardLayout from '@/app/pages/family_application/FamilyWizardLayout'
import { Label } from '@/shad/ui/label'
import { Textarea } from '@/shad/ui/textarea'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { Expense, ExpenseCategoriesSelection } from './category-selector'

// Types
export const ExpenseFormSchema = z.object({
  comments: z.string(),
  expenseCategories: z.array(z.object({
    amount: z.number(),
    id: z.number(),
    name: z.string(),
  })).min(1),
  invoiceNumber: z.string(),
})

export type TExpenseForm = z.infer<typeof ExpenseFormSchema>

interface ExpenseFormProps {
  expenses: Expense[]
  onSubmit: (data: TExpenseForm) => void
  onBack: () => void
  submitStatus?: 'idle' | 'loading' | 'success' | 'error',
  invoiceLabel: string
  defaultValues: TExpenseForm
}

export function SelectExpenseCategoriesForm({
  expenses,
  onSubmit,
  onBack,
  invoiceLabel,
  defaultValues,
}: ExpenseFormProps) {
  const { t } = useTranslation()

  const {
    control,
    handleSubmit,
    formState: { errors },
    register,
    watch,
    getValues,
    setValue,
  } = useForm<TExpenseForm>({
    resolver: zodResolver(ExpenseFormSchema),
    defaultValues: defaultValues,
  })

  const handleCheckboxChange = (expense: number) => {
    const currentValues = getValues('expenseCategories')
    if (currentValues.some((item) => item.id === expense)) {
      setValue(
        'expenseCategories',
        currentValues.filter((item) => item.id !== expense)
      )
    } else {
      setValue('expenseCategories', [...currentValues, {
        id: expense,
        amount: 0,
        name: expenses.find((item) => item.id === expense)?.name ?? ""
      }])
    }
  }



  watch('expenseCategories')
  return (
    <FamilyWizardLayout>
      <FamilyWizardLayout.Header>
        {t('please_select_expense_category')}
      </FamilyWizardLayout.Header>

      <FamilyWizardLayout.Content className="w-full">
        <form className="space-y-8" onSubmit={handleSubmit(onSubmit)}>
          <ExpenseCategoriesSelection
            selectedCategories={getValues('expenseCategories')}
            expenses={expenses}
            control={control}
            error={
              errors?.expenseCategories?.message &&
              t(errors.expenseCategories?.message)
            }
            handleCheckboxChange={handleCheckboxChange}
          />

          <div className="space-y-1">
            <Label>{t('comments_and_descriptions')}</Label>
            <Textarea {...register('comments')} />
            {errors.comments && (
              <FormErrorMessage
                message={errors.comments.message}
                className="text-red-500 mt-2"
              />
            )}
          </div>

          {/* Invoice Number Field */}
          <div className="space-y-1">
            <Label className="cursor-pointer">{invoiceLabel}</Label>
            <Input type="number" {...register('invoiceNumber')} />
            {errors.invoiceNumber && (
              <FormErrorMessage
                message={errors.invoiceNumber.message}
                className="text-red-500 mt-2"
              />
            )}
          </div>

          {/* Action Bar */}
          <div className="mt-4 flex gap-2">
            <FamilyWizardLayout.ActionBar className="justify-center gap-2">
              <Button
                onClick={onBack}
                className={buttonVariants({
                  variant: 'outline',
                })}
                type="button"
              >
                {t('back')}
              </Button>
              <div>
                <Button type="submit">{t('continue')}</Button>
              </div>
            </FamilyWizardLayout.ActionBar>
          </div>
        </form>
      </FamilyWizardLayout.Content>
    </FamilyWizardLayout>
  )
}
